import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { Fingerprint, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, tokens } from '../lib/api';
import type { Parent, PasskeyPolicy } from '../lib/types';
import { Fold } from '../ui/Collapsible';
import { useParent } from './ParentContext';

// Passkeys (#417): sign in with Face ID, Touch ID, Windows Hello, a phone or a security key.

export interface PasskeyItem {
  id: string;
  name: string;
  createdAt: number;
  lastUsedAt: number | null;
  backedUp: boolean;
}

/** The browser's answer when someone closes the passkey prompt: not an error worth showing. */
const cancelled = (e: unknown) => e instanceof Error && (e.name === 'NotAllowedError' || e.name === 'AbortError');

/** A sensible name for a new passkey, from the device it's being added on. */
function deviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Mac OS X/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'This device';
}

/** Ask the browser for a passkey and save it on the account. Resolves with the account's passkeys, or null if cancelled. */
export async function addPasskey(name: string): Promise<PasskeyItem[] | null> {
  const { challengeId, options } = await api.post<{ challengeId: string; options: Parameters<typeof startRegistration>[0]['optionsJSON'] }>('/api/me/passkeys/options');
  let response;
  try {
    response = await startRegistration({ optionsJSON: options });
  } catch (e) {
    if (cancelled(e)) return null;
    throw e;
  }
  return (await api.post<{ passkeys: PasskeyItem[] }>('/api/me/passkeys', { challengeId, name, response })).passkeys;
}

/** "Sign in with a passkey" on the sign-in page. */
export function PasskeySignIn({ onSignedIn, onError }: { onSignedIn: (p: Parent) => void; onError: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  if (!browserSupportsWebAuthn()) return null;
  async function signIn() {
    setBusy(true);
    onError('');
    try {
      const { challengeId, options } = await api.post<{ challengeId: string; options: Parameters<typeof startAuthentication>[0]['optionsJSON'] }>('/api/auth/passkey/options');
      let response;
      try {
        response = await startAuthentication({ optionsJSON: options });
      } catch (e) {
        if (cancelled(e)) return;
        throw e;
      }
      const { token } = await api.post<{ token: string }>('/api/auth/passkey', { challengeId, response });
      tokens.setParent(token);
      onSignedIn(await api.get<Parent>('/api/me'));
    } catch (e) {
      onError(e instanceof Error ? e.message : 'That passkey didn’t work.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <button type="button" className="btn big" onClick={() => void signIn()} disabled={busy}>
      <Fingerprint size={18} /> {busy ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
    </button>
  );
}

/** Settings → Account security → Passkeys: add, rename and remove. */
export function PasskeySettings() {
  const { parent, setParent, toast } = useParent();
  const [items, setItems] = useState<PasskeyItem[] | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    api
      .get<{ enabled: boolean; passkeys: PasskeyItem[] }>('/api/me/passkeys')
      .then((r) => (setItems(r.passkeys), setEnabled(r.enabled)))
      .catch(() => setItems([]));
  }, []);
  const refreshMe = () => api.get<Parent>('/api/me').then(setParent).catch(() => undefined);

  if (!enabled && !items?.length) return null;
  const supported = browserSupportsWebAuthn();

  async function add() {
    setBusy(true);
    try {
      const next = await addPasskey(deviceName());
      if (next) {
        setItems(next);
        toast({ text: 'Passkey added. You can sign in with it now.', tone: 'good' });
        void refreshMe();
      }
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function act(fn: () => Promise<{ passkeys: PasskeyItem[] }>, done: string) {
    try {
      setItems((await fn()).passkeys);
      toast({ text: done, tone: 'good' });
      void refreshMe();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <Fold
      id="passkeys"
      title="Passkeys"
      summary={items ? (items.length ? `${items.length} passkey${items.length === 1 ? '' : 's'}` : 'None yet') : undefined}
    >
      <p className="muted small">
        Sign in with your face, fingerprint or screen lock instead of a password and code. A passkey stays on your device
        (or syncs with your phone’s keychain); nobody can guess it. Your password still works too.
      </p>
      {!items ? (
        <p className="muted">Loading…</p>
      ) : (
        !!items.length && (
          <ul className="passkey-list">
            {items.map((k) => (
              <li key={k.id} className="row-between">
                {renaming === k.id ? (
                  <form
                    className="row grow"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void act(() => api.patch(`/api/me/passkeys/${k.id}`, { name: newName }), 'Renamed.').then(() => setRenaming(null));
                    }}
                  >
                    <input value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={60} aria-label="Passkey name" autoFocus />
                    <button className="btn small primary">Save</button>
                    <button type="button" className="btn small" onClick={() => setRenaming(null)}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <span className="grow">
                      <strong>{k.name}</strong>
                      <span className="muted small block">
                        Added {new Date(k.createdAt).toLocaleDateString()} ·{' '}
                        {k.lastUsedAt ? `last used ${new Date(k.lastUsedAt).toLocaleDateString()}` : 'not used yet'}
                        {k.backedUp && ' · synced'}
                      </span>
                    </span>
                    <button className="btn small" title="Rename" aria-label={`Rename ${k.name}`} onClick={() => (setRenaming(k.id), setNewName(k.name))}>
                      <Pencil size={15} />
                    </button>
                    <button
                      className="btn small danger-text"
                      title="Remove"
                      aria-label={`Remove ${k.name}`}
                      onClick={() => confirm(`Remove the passkey “${k.name}”? You won’t be able to sign in with it anymore.`) && void act(() => api.del(`/api/me/passkeys/${k.id}`), 'Passkey removed.')}
                    >
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )
      )}
      {enabled &&
        (supported ? (
          <div>
            <button className="btn" onClick={() => void add()} disabled={busy}>
              <Plus size={16} /> {busy ? 'Follow your device’s prompt…' : 'Add a passkey'}
            </button>
          </div>
        ) : (
          <p className="muted small">This browser can’t create passkeys. Try a recent Chrome, Edge, Safari or Firefox.</p>
        ))}
      {parent.mfaRequired && <p className="muted small">On this server, a passkey also counts as your two-factor sign-in.</p>}
    </Fold>
  );
}

/** Admin → Server settings → Sign-in security → Passkeys (#417). */
export function PasskeyPolicySettings({ value, onChange, publicUrl }: { value: PasskeyPolicy; onChange: (v: PasskeyPolicy) => void; publicUrl: string }) {
  return (
    <div className="stack">
      <label className="check">
        <input type="checkbox" checked={value.enabled} onChange={(e) => onChange({ ...value, enabled: e.target.checked })} />
        <span>
          Let parents sign in with passkeys
          <span className="muted small block">Face ID, Touch ID, Windows Hello, a phone or a security key, set up in Settings → Account security.</span>
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={value.countAsMfa} disabled={!value.enabled} onChange={(e) => onChange({ ...value, countAsMfa: e.target.checked })} />
        <span>
          A passkey counts as two-factor sign-in
          <span className="muted small block">Where two-factor is required, a passkey meets it; an authenticator app isn’t needed as well.</span>
        </span>
      </label>
      {value.enabled && !publicUrl && (
        <p className="small notice-text">
          Set the Public address (General) first: passkeys belong to this server’s address, so they stop working if it changes.
        </p>
      )}
    </div>
  );
}
